// Canvas do Team Map: viewport (overflow-hidden) + world (translate+scale) +
// render de edges (SVG) e nós (divs), pan/zoom/drag estilo n8n. Zero dependência nova.

import { useEffect, useRef } from 'preact/hooks';
import { useComputed, useSignal } from '@preact/signals';
import type { TeamSummary, AgentSummary } from '../../types/agno-api';
import {
  positions,
  pan,
  zoom,
  toolsCollapsed,
  draggingId,
  graph,
  clampZoom,
  screenToWorld,
  getPos,
  initFlow,
  relayout,
} from './flowState';
import { computeFit, type FlowNodeModel } from './layout';
import { FlowNode } from './FlowNode';
import { FlowEdge, EdgeMarkers, rightHandle, leftHandle } from './FlowEdge';
import { Toolbar } from './Toolbar';

type SelectPayload = TeamSummary | AgentSummary;

const DRAG_THRESHOLD = 5; // px na tela: abaixo disso, pointerup = clique.

export function TeamFlow({
  team,
  members,
  selectedId,
  onSelect,
  onClose,
}: {
  team: TeamSummary;
  members: AgentSummary[];
  selectedId: string | null;
  onSelect: (payload: SelectPayload) => void;
  onClose: () => void;
}) {
  const viewportRef = useRef<HTMLDivElement>(null);

  // (Re)inicializa o grafo quando muda o team (ou a sua composição de members).
  const teamKey = `${team.id}:${members.map((m) => m.id).join(',')}`;
  useEffect(() => {
    initFlow(team, members);
    // Auto-fit após o primeiro paint, quando o viewport já tem dimensão.
    requestAnimationFrame(() => doFit());
  }, [teamKey]);

  // Esc fecha o painel de detalhe.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function viewportRect(): DOMRect {
    return (
      viewportRef.current?.getBoundingClientRect() ??
      new DOMRect(0, 0, window.innerWidth, window.innerHeight)
    );
  }

  function doFit() {
    const rect = viewportRect();
    const fit = computeFit(graph.value.nodes, getPos, rect.width, rect.height, clampZoom);
    zoom.value = fit.zoom;
    pan.value = fit.pan;
  }

  // Zoom ancorado num ponto da tela (cursor ou centro do viewport).
  function zoomAt(nextZoomRaw: number, screenX: number, screenY: number) {
    const rect = viewportRect();
    const next = clampZoom(nextZoomRaw);
    // Mundo sob o ponto antes do zoom (conversão centralizada).
    const w = screenToWorld(screenX, screenY, rect);
    // Reposiciona o pan para que esse ponto continue sob o cursor após o zoom.
    pan.value = {
      x: screenX - rect.left - w.x * next,
      y: screenY - rect.top - w.y * next,
    };
    zoom.value = next;
  }

  function zoomButton(delta: number) {
    const rect = viewportRect();
    zoomAt(zoom.value + delta, rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  // --- PAN (fundo do viewport) ---
  const panStart = useRef<{ x: number; y: number; pan: { x: number; y: number } } | null>(null);
  const isPanning = useSignal(false);

  function onViewportPointerDown(e: PointerEvent) {
    // Só inicia pan se o alvo for o próprio fundo (nós param a propagação).
    if (e.button !== 0) return;
    panStart.current = { x: e.clientX, y: e.clientY, pan: { ...pan.value } };
    isPanning.value = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onViewportPointerMove(e: PointerEvent) {
    if (!panStart.current) return;
    pan.value = {
      x: panStart.current.pan.x + (e.clientX - panStart.current.x),
      y: panStart.current.pan.y + (e.clientY - panStart.current.y),
    };
  }
  function onViewportPointerUp(e: PointerEvent) {
    panStart.current = null;
    isPanning.value = false;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* capture pode já ter sido liberado */
    }
  }

  // --- ZOOM (wheel) ---
  // Listener nativo non-passive para poder preventDefault() escopado ao viewport.
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      zoomAt(zoom.value * Math.pow(1.1, -e.deltaY / 100), e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // --- DRAG de nó ---
  const dragRef = useRef<{
    id: string;
    startScreen: { x: number; y: number };
    startWorld: { x: number; y: number };
    moved: number;
    payload: SelectPayload;
  } | null>(null);

  function startNodeDrag(node: FlowNodeModel, e: PointerEvent) {
    e.stopPropagation(); // não dispara pan
    if (e.button !== 0) return;
    const start = getPos(node);
    // payload para clique: tool → agente dono; senão o próprio.
    const payload: SelectPayload =
      node.kind === 'tool'
        ? (node.parentAgent as AgentSummary)
        : (node.payload as SelectPayload);
    dragRef.current = {
      id: node.id,
      startScreen: { x: e.clientX, y: e.clientY },
      startWorld: start,
      moved: 0,
      payload,
    };
    draggingId.value = node.id;
    // setPointerCapture no próprio nó → move/up subsequentes chegam neste elemento.
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onNodePointerMove(e: PointerEvent) {
    const d = dragRef.current;
    if (!d) return;
    const dxScreen = e.clientX - d.startScreen.x;
    const dyScreen = e.clientY - d.startScreen.y;
    d.moved = Math.max(d.moved, Math.hypot(dxScreen, dyScreen));
    // Delta em coords de mundo = delta de tela / zoom.
    const next = new Map(positions.value);
    next.set(d.id, {
      x: d.startWorld.x + dxScreen / zoom.value,
      y: d.startWorld.y + dyScreen / zoom.value,
    });
    positions.value = next;
  }
  function onNodePointerUp(e: PointerEvent) {
    const d = dragRef.current;
    dragRef.current = null;
    draggingId.value = null;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    if (!d) return;
    if (d.moved < DRAG_THRESHOLD) {
      onSelect(d.payload); // foi clique, não drag
    }
  }

  // --- Toolbar handlers ---
  function toggleToolsGlobal() {
    toolsCollapsed.value = !toolsCollapsed.value;
    relayout(team, members);
    requestAnimationFrame(() => doFit());
  }
  // Expande/recolhe (global) a partir do chip de um agente. Mínimo viável: toggle global.
  function toggleToolsFromAgent() {
    toolsCollapsed.value = false;
    relayout(team, members);
    requestAnimationFrame(() => doFit());
  }

  // Edges conectados ao nó selecionado/em-drag → realce.
  const highlightId = useComputed(() => draggingId.value ?? selectedId);

  // `getPos` (chamado no map abaixo) lê `positions.value`, então o componente
  // re-renderiza quando uma posição muda durante o drag.
  const g = graph.value;

  const worldStyle = {
    transform: `translate(${pan.value.x}px, ${pan.value.y}px) scale(${zoom.value})`,
    transformOrigin: '0 0' as const,
  };
  const gridStyle = {
    // Dot derivado do foreground (não do border puro, que some no dark):
    // mistura leve com o fundo garante contraste discreto em light e dark.
    backgroundImage:
      'radial-gradient(color-mix(in oklab, var(--color-foreground) 22%, transparent) 1px, transparent 1px)',
    backgroundSize: `${24 * zoom.value}px ${24 * zoom.value}px`,
    backgroundPosition: `${pan.value.x}px ${pan.value.y}px`,
  };

  const nodeById = new Map(g.nodes.map((n) => [n.id, n] as const));

  return (
    <div
      ref={viewportRef}
      class={`relative flex-1 overflow-hidden bg-background ${
        isPanning.value ? 'cursor-grabbing' : 'cursor-grab'
      }`}
      style={gridStyle}
      onPointerDown={onViewportPointerDown}
      onPointerMove={onViewportPointerMove}
      onPointerUp={onViewportPointerUp}
    >
      <Toolbar
        zoomPct={Math.round(zoom.value * 100)}
        collapsed={toolsCollapsed.value}
        onZoomIn={() => zoomButton(0.2)}
        onZoomOut={() => zoomButton(-0.2)}
        onFit={doFit}
        onToggleTools={toggleToolsGlobal}
      />

      {/* world */}
      <div class="absolute top-0 left-0" style={worldStyle}>
        {/* edges (z-0) */}
        <svg class="absolute top-0 left-0 overflow-visible pointer-events-none z-0">
          <EdgeMarkers />
          {g.edges.map((edge) => {
            const src = nodeById.get(edge.sourceId);
            const tgt = nodeById.get(edge.targetId);
            if (!src || !tgt) return null;
            const sp = rightHandle(src, getPos(src));
            const tp = leftHandle(tgt, getPos(tgt));
            const hl =
              highlightId.value === edge.sourceId || highlightId.value === edge.targetId;
            return <FlowEdge key={edge.id} edge={edge} source={sp} target={tp} highlighted={hl} />;
          })}
        </svg>

        {/* nodes (z-10) */}
        {g.nodes.map((node) => (
          <FlowNode
            key={node.id}
            node={node}
            pos={getPos(node)}
            selected={selectedId === node.id}
            dragging={draggingId.value === node.id}
            onPointerDown={(e) => startNodeDrag(node, e)}
            onPointerMove={onNodePointerMove}
            onPointerUp={onNodePointerUp}
            onToggleTools={node.kind === 'agent' ? toggleToolsFromAgent : undefined}
          />
        ))}
      </div>

      {members.length === 0 && (
        <div class="absolute bottom-3 left-3 z-30 text-xs text-muted-foreground font-mono bg-card/80 border border-border rounded px-2 py-1">
          team sem subagentes expostos
        </div>
      )}
    </div>
  );
}
